import { useState, useEffect, useCallback } from 'react';
import api, { BACKEND_URL } from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Receipt, CheckCircle2, XCircle, AlertCircle, Download, FileText, Search } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

export default function AdminExpensesTab() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showReview, setShowReview] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [csvMonth, setCsvMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await api.get('/expenses');
      setExpenses(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleAction = async (expenseId, status) => {
    try {
      await api.put(`/expenses/${expenseId}/status`, { status, admin_notes: adminNote });
      toast.success(`Expense ${status}`);
      setShowReview(null);
      setAdminNote('');
      fetchExpenses();
    } catch { toast.error('Failed to update'); }
  };

  const downloadCSV = async () => {
    try {
      const res = await api.get(`/expenses/report/csv?month=${csvMonth}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SplashAble_Expenses_${csvMonth}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded!');
    } catch { toast.error('Failed to download report'); }
  };

  const filtered = expenses.filter(e => {
    if (filter !== 'all' && e.status !== filter) return false;
    if (search && !e.item_description.toLowerCase().includes(search.toLowerCase()) && !e.instructor_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pending = expenses.filter(e => e.status === 'pending');
  const approved = expenses.filter(e => e.status === 'approved');
  const totalApproved = approved.reduce((sum, e) => sum + (e.total || 0), 0);
  const totalTax = approved.reduce((sum, e) => sum + (e.tax || 0), 0);

  if (loading) return <div className="flex items-center justify-center p-8"><img src="/logo.png" alt="Loading" className="w-10 h-10 animate-pulse" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-expenses-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="rounded-2xl border-amber-100">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-extrabold text-amber-600">{pending.length}</p>
            <p className="text-xs text-slate-500">Pending</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-green-100">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-extrabold text-green-600">{approved.length}</p>
            <p className="text-xs text-slate-500">Approved</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-green-100">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-extrabold text-green-700">${totalApproved.toFixed(2)}</p>
            <p className="text-xs text-slate-500">Total Approved</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-slate-100">
          <CardContent className="p-3 text-center">
            <p className="text-xl font-extrabold text-slate-600">${totalTax.toFixed(2)}</p>
            <p className="text-xs text-slate-500">Total Tax</p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize ${filter === f ? 'bg-[#1E1B4B] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`filter-${f}`}>
              {f} {f === 'pending' && pending.length > 0 && `(${pending.length})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="rounded-full pl-9 h-9 w-48" data-testid="expense-search" />
          </div>
        </div>
      </div>

      {/* CSV Download */}
      <Card className="rounded-2xl border-slate-100">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Receipt className="w-5 h-5 text-[#22D3EE]" />
          <span className="text-sm font-bold text-slate-700">Monthly Report</span>
          <Input type="month" value={csvMonth} onChange={e => setCsvMonth(e.target.value)} className="rounded-xl h-9 w-44" data-testid="csv-month-picker" />
          <Button onClick={downloadCSV} variant="outline" className="rounded-full gap-2 font-bold text-sm border-[#22D3EE] text-[#0E7490] hover:bg-[#ECFEFF]" data-testid="download-csv-btn">
            <Download className="w-4 h-4" /> Download CSV
          </Button>
        </CardContent>
      </Card>

      {/* Expense List */}
      {filtered.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Receipt className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-lg font-bold text-slate-400">No expenses found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="admin-expenses-list">
          {filtered.map(expense => (
            <Card key={expense.expense_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`admin-expense-${expense.expense_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-slate-800 text-sm">{expense.item_description}</h3>
                      <Badge className={`rounded-full text-xs capitalize ${STATUS_STYLES[expense.status]}`}>{expense.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-1">By: <strong>{expense.instructor_name}</strong></p>
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span>Amount: <strong>${expense.amount?.toFixed(2)}</strong></span>
                      <span>Tax: <strong>${expense.tax?.toFixed(2)}</strong></span>
                      <span className="font-bold text-slate-800">Total: ${expense.total?.toFixed(2)}</span>
                    </div>
                    {expense.notes && <p className="text-xs text-slate-500 mt-1">{expense.notes}</p>}
                    {expense.admin_notes && <p className="text-xs text-purple-600 mt-1 bg-purple-50 rounded-lg px-2 py-1">Note: {expense.admin_notes}</p>}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>{new Date(expense.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {expense.receipt_filename && (
                        <a href={`${BACKEND_URL}/api/uploads/${expense.receipt_filename}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#0E7490] hover:underline">
                          <FileText className="w-3 h-3" /> Receipt
                        </a>
                      )}
                    </div>
                  </div>
                  {expense.status === 'pending' && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" className="rounded-full bg-green-500 hover:bg-green-600 text-white gap-1 h-8" onClick={() => { setShowReview(expense); setAdminNote(''); }} data-testid={`review-expense-${expense.expense_id}`}>
                        Review
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!showReview} onOpenChange={() => setShowReview(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#22D3EE]" /> Review Expense
            </DialogTitle>
          </DialogHeader>
          {showReview && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-bold text-slate-800">{showReview.item_description}</p>
                <p className="text-sm text-slate-500 mt-1">By: {showReview.instructor_name}</p>
                <div className="flex gap-4 mt-2 text-sm">
                  <span>Amount: <strong>${showReview.amount?.toFixed(2)}</strong></span>
                  <span>Tax: <strong>${showReview.tax?.toFixed(2)}</strong></span>
                  <span className="font-bold">Total: ${showReview.total?.toFixed(2)}</span>
                </div>
                {showReview.notes && <p className="text-xs text-slate-500 mt-2">{showReview.notes}</p>}
                {showReview.receipt_filename && (
                  <a href={`${BACKEND_URL}/api/uploads/${showReview.receipt_filename}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#0E7490] hover:underline text-sm mt-2">
                    <FileText className="w-4 h-4" /> View Receipt
                  </a>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Admin Notes (optional)</label>
                <Textarea value={adminNote} onChange={e => setAdminNote(e.target.value)} placeholder="Add a note..." className="rounded-xl mt-1" rows={2} data-testid="admin-expense-note" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold gap-2" onClick={() => handleAction(showReview.expense_id, 'rejected')} data-testid="reject-expense-btn">
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
                <Button className="rounded-full bg-green-500 hover:bg-green-600 text-white font-bold gap-2" onClick={() => handleAction(showReview.expense_id, 'approved')} data-testid="approve-expense-btn">
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
